import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import {createPortal} from 'react-dom';

export type PanelConfig = {
  defaultSize?: number;
  minSize?: number;
  maxSize?: number;
};

type SplitLayoutContextType = {
  headerPortal: HTMLElement | null;
  leftPortal: HTMLElement | null;
  rightPortal: HTMLElement | null;
  setHeaderPortal: (el: HTMLElement | null) => void;
  setLeftPortal: (el: HTMLElement | null) => void;
  setRightPortal: (el: HTMLElement | null) => void;
  leftPanelConfig: PanelConfig;
  rightPanelConfig: PanelConfig;
};

const defaultLeftPanelConfig: PanelConfig = {
  defaultSize: 65,
  minSize: 30,
  maxSize: 70,
};

const defaultRightPanelConfig: PanelConfig = {
  defaultSize: 35,
  minSize: 30,
  maxSize: 70,
};

export const SplitLayoutContext = createContext<SplitLayoutContextType | null>(
  null,
);

export const useSplitLayout = () => {
  const context = useContext(SplitLayoutContext);
  if (!context) {
    throw new Error('useSplitLayout must be used within SplitLayout');
  }
  return context;
};

export const useSplitLayoutState = () => {
  const [headerPortal, setHeaderPortal] = useState<HTMLElement | null>(null);
  const [leftPortal, setLeftPortal] = useState<HTMLElement | null>(null);
  const [rightPortal, setRightPortal] = useState<HTMLElement | null>(null);

  return {
    headerPortal,
    leftPortal,
    rightPortal,
    setHeaderPortal,
    setLeftPortal,
    setRightPortal,
    leftPanelConfig: defaultLeftPanelConfig,
    rightPanelConfig: defaultRightPanelConfig,
  };
};

type PortalProps = {
  children: ReactNode;
};

export const SplitHeader = ({children}: PortalProps) => {
  const {headerPortal} = useSplitLayout();
  return headerPortal ? createPortal(children, headerPortal) : null;
};

export const SplitLeftPanel = ({children}: PortalProps) => {
  const {leftPortal} = useSplitLayout();
  return leftPortal ? createPortal(children, leftPortal) : null;
};

export const SplitRightPanel = ({children}: PortalProps) => {
  const {rightPortal} = useSplitLayout();
  return rightPortal ? createPortal(children, rightPortal) : null;
};

type PortalContainerProps = {
  id: string;
  onMount: (el: HTMLElement | null) => void;
  children?: ReactNode;
};

export const PortalContainer = ({
  id,
  onMount,
  children,
}: PortalContainerProps) => {
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      onMount(node);
    },
    [onMount],
  );

  return (
    <div ref={ref} id={id} style={{display: 'contents'}}>
      {children}
    </div>
  );
};
